import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import CryptoJS from "crypto-js";
import classNames from "classnames";
import { Modal } from "../../../molecules";
import { MODAL_SIZE } from "../modals.constants";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { EMPTY_STRING, REGEX } from "../../../../constants";
import { Field, Form, Formik } from "formik";
import {
  createSecret,
  updateSecretKey,
} from "../../../../pages/DataModels/dataModels.actions";
import {
  fetchENVSecrets,
  selectSelectedDataModelId,
  selectSelectedSecretKey,
  setSelectedSecretKey,
  selectSelectedSecrets,
} from "../../../../pages/DataModels";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { VALIDATION_MESSAGES } from "../../../../constants";

const { setSelectedModal } = modalActions;
const { MD } = MODAL_SIZE;
const { SPECIAL_CHARACTERS, SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START } =
  REGEX;
const secretPass = "Xkh87juMlI89G";
const { ADD_SECRET, UPDATE_SECRET } = SPINNER_NAMES;
const { NAME_SHOULD_CONTAIN, NAME_SHOULD_NOT_CONTAIN, MIN_CHAR_LENGTH, SECRET_KEY_NAME_REQUIRED, 
  EXISTS_CHECK, SECRET_KEY_EXIST, ENTER_SECRET_KEY, NAME } = VALIDATION_MESSAGES

const AddSecretsModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedKey = useSelector(selectSelectedSecretKey);
  const selectedSecrets = useSelector(selectSelectedSecrets);
  const dataModelId = useSelector(selectSelectedDataModelId);

  const [selectedEnvVariable, setSelectedEnvVariable] = useState();
  const [selectedSecretsNames, setSelectedSecretsNames] = useState();

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [isEmptySecret, setIsEmptySecret] = useState(false);
  const initialValues = {
    name: selectedEnvVariable?.name || EMPTY_STRING,
    secret: EMPTY_STRING,
  };

  useEffect(() => {
    selectedKey && setSelectedEnvVariable(selectedKey);
    selectedSecrets && setSelectedSecretsNames(selectedSecrets.map((secret) => secret.name?.toLowerCase()));
  }, [selectedKey, selectedSecrets]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
    dispatch(setSelectedSecretKey(null));
  }, [dispatch]);

  
  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(
        SPECIAL_CHARACTERS,
        NAME_SHOULD_NOT_CONTAIN
      )
      .min(3, MIN_CHAR_LENGTH)
      .required(SECRET_KEY_NAME_REQUIRED)
      .test(
        EXISTS_CHECK,
        SECRET_KEY_EXIST,
        (value) =>
          !selectedSecretsNames.includes(value?.toLowerCase().trim()) ||
          selectedEnvVariable?.name?.toLowerCase() === value.toLocaleLowerCase().trim()
      ),

    secret: Yup.string().when(NAME, {
      is: () => !selectedKey,
      then: Yup.string().required(ENTER_SECRET_KEY),
    }),
  });

  const handleOnSubmit = (values) => {
    let { secret, name } = values;
    const encryptedKey = CryptoJS.AES.encrypt(
      secret,
      secretPass
    ).toString();
    setIsEmptySecret(secret);
    const formattedData = { name: name.trim(), secret: encryptedKey, dataModelId };
    dispatch(
      selectedKey
        ? updateSecretKey({ id: selectedKey.id, data: formattedData })
        : createSecret({ id: dataModelId, data: formattedData })
    ).then(() => {
      dispatch(fetchENVSecrets(dataModelId));
      dispatch(setSelectedModal(null));
      !selectedKey && navigate(`/data-models/${dataModelId}/settings/secrets`);
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        handleReset,
        touched,
        errors,
        isValid,
        dirty
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              type="button"
              className="btn btn-primary-light mr-1"
              onClick={() => {
                handleReset();
                handleClose();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isValid || !dirty || isSpinnerActive || isEmptySecret}
              onClick={(e) => {
                handleSubmit(e);
              }}
            >
              <Spinner
                name={selectedKey ? UPDATE_SECRET : ADD_SECRET}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {selectedKey ? "Update" : "Save"}
              </Spinner>
            </button>
          </div>
        );
        return (
          <Modal
            title="Add Environment Secrets"
            handleClose={handleClose}
            size={MD}
            footer={footer}
          >
            <Form>
              <div className="modal-body p-6">
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Secret Name
                    </label>
                    <Field
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Secret Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Secret Key
                    </label>
                    <Field
                      as="textarea"
                      className={classNames(
                        "form-control border-2 disabled:opacity-75 h-28",
                        {
                          "border-red-600":
                            touched.secret && errors.secret,
                        }
                      )}
                      placeholder="Add Secret Key"
                      name="secret"
                      value={values.secret}
                      onChange={(e) => {
                        if (e.target.value?.trim() === EMPTY_STRING)
                          setIsEmptySecret(true);
                        else setIsEmptySecret(false);
                        return handleChange(e);
                      }}
                    />
                    {touched.secret && errors.secret && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.secret}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default AddSecretsModal;
